/**
 * Server-state hooks for the sampling feature — the ONLY place this feature touches the API client.
 * Queries are lab-scoped via qk; every mutation invalidates the affected keys (L-1, CRUD-complete).
 *
 * Endpoint map (server/src/modules/sampling-*-routes — bare under /api/v1):
 *   GET  /sampling/files                        -> WaitingBoard          (sampling.file.read)
 *   GET  /sampling/salles                       -> WaitingRoom[]         (sampling.file.read)
 *   GET  /sampling/files-attente                -> BenchFile[]           (sampling.file.read)
 *   POST /sampling/files-attente/appeler-suivant                        (sampling.prelevement.update)
 *   POST /sampling/tickets/:id/appeler|absent|acheminer|terminer        (sampling.prelevement.update)
 *   POST /sampling/tickets/:id/prelever         -> PreleverResult        (sampling.prelevement.create)
 *   POST /sampling/tickets/:id/reprendre        -> Prelevement           (sampling.prelevement.create)
 *   POST /sampling/scan                         -> Tube                  (sampling.prelevement.read)
 *   POST /sampling/tubes/:id/enfiler            -> EnqueueResult         (sampling.prelevement.create)
 *   POST /sampling/matricule/vignette           -> Vignette              (sampling.matricule.print)
 */
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type {
  BenchFile,
  DrawInput,
  EnqueueResult,
  PreleverResult,
  Tube,
  Vignette,
  WaitingBoard,
  WaitingRoom,
} from "./types";

/** The signed-in lab id (queries are scoped to it; a lab switch invalidates the whole scope). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? null);
}

/** The waiting-room board. Optionally pinned to a room; otherwise the lab default room. */
export function useBoard(waitingRoomId?: string | null) {
  const labId = useLabId();
  const query = waitingRoomId ? { waiting_room_id: waitingRoomId } : undefined;
  return useApiQuery<WaitingBoard>(
    qk.scoped(labId, "sampling", "board", waitingRoomId ?? "default"),
    "/sampling/files",
    {
      ...(query ? { query } : {}),
      // The board is a live operational surface — keep it fresh.
      staleTime: 5_000,
    },
  );
}

/** The lab's waiting rooms (for the board room-picker). */
export function useRooms() {
  const labId = useLabId();
  return useApiQuery<WaitingRoom[]>(qk.scoped(labId, "sampling", "rooms"), "/sampling/salles");
}

/** The lab's per-discipline bench files. */
export function useBenchFiles() {
  const labId = useLabId();
  return useApiQuery<BenchFile[]>(
    qk.scoped(labId, "sampling", "benches"),
    "/sampling/files-attente",
    { staleTime: 10_000 },
  );
}

/** Keys to invalidate after any ticket lifecycle change (every board + bench). */
function ticketKeys(labId: string | null) {
  return [
    qk.scoped(labId, "sampling", "board"),
    qk.scoped(labId, "sampling", "benches"),
  ] as const;
}

/** Call the next waiting ticket in a room (urgent before routine, then numero FIFO). */
export function useAppelerSuivant() {
  const labId = useLabId();
  return useApiMutation<unknown, { waiting_room_id: string }>(
    (vars) => api.post("/sampling/files-attente/appeler-suivant", vars),
    { invalidate: ticketKeys(labId) },
  );
}

/** Call one specific ticket by id (en_attente -> appele). */
export function useAppeler() {
  const labId = useLabId();
  return useApiMutation<unknown, { ticketId: string }>(
    ({ ticketId }) => api.post(`/sampling/tickets/${ticketId}/appeler`),
    { invalidate: ticketKeys(labId) },
  );
}

/** Mark a called ticket absent (no-show) — re-queues the SAME entry, numero preserved. */
export function useAbsent() {
  const labId = useLabId();
  return useApiMutation<unknown, { ticketId: string }>(
    ({ ticketId }) => api.post(`/sampling/tickets/${ticketId}/absent`),
    { invalidate: ticketKeys(labId) },
  );
}

/** Record the draw: one line per tube (effectue/partiel/echoue). */
export function usePrelever() {
  const labId = useLabId();
  return useApiMutation<PreleverResult, { ticketId: string; draws: DrawInput[] }>(
    ({ ticketId, draws }) => api.post(`/sampling/tickets/${ticketId}/prelever`, { draws }),
    { invalidate: ticketKeys(labId) },
  );
}

/** Redraw on a fresh (reception-reissued) tube — a NEW, append-only Prelevement. */
export function useReprendre() {
  const labId = useLabId();
  return useApiMutation<unknown, { ticketId: string; tube_id: string; volume?: number }>(
    ({ ticketId, tube_id, volume }) =>
      api.post(`/sampling/tickets/${ticketId}/reprendre`, {
        tube_id,
        ...(volume !== undefined ? { volume } : {}),
      }),
    { invalidate: ticketKeys(labId) },
  );
}

/** Route a prelevé ticket to the lab (preleve -> achemine). */
export function useAcheminer() {
  const labId = useLabId();
  return useApiMutation<unknown, { ticketId: string }>(
    ({ ticketId }) => api.post(`/sampling/tickets/${ticketId}/acheminer`),
    { invalidate: ticketKeys(labId) },
  );
}

/** Close a routed ticket (achemine -> termine). */
export function useTerminer() {
  const labId = useLabId();
  return useApiMutation<unknown, { ticketId: string }>(
    ({ ticketId }) => api.post(`/sampling/tickets/${ticketId}/terminer`),
    { invalidate: ticketKeys(labId) },
  );
}

/** Resolve a scanned tube (QR raw OR matricule) -> Tube. Imperative (one-shot, not cached). */
export function useScan() {
  return useApiMutation<Tube, { raw_token?: string; matricule?: string }>((vars) =>
    api.post("/sampling/scan", vars),
  );
}

/** Fan a resolved tube into its per-discipline benches (offline = primary discipline only). */
export function useEnqueueTube() {
  const labId = useLabId();
  return useApiMutation<EnqueueResult, { tubeId: string; offline?: boolean }>(
    ({ tubeId, offline }) =>
      api.post(`/sampling/tubes/${tubeId}/enfiler`, offline ? { offline } : {}),
    { invalidate: [qk.scoped(labId, "sampling", "benches")] },
  );
}

/** Build the matricule vignette print payload (R-10). */
export function useVignette() {
  return useApiMutation<
    Vignette,
    {
      matricule?: string;
      token?: string;
      nom?: string;
      prenom?: string;
      demande_ref?: string;
    }
  >((vars) => api.post("/sampling/matricule/vignette", vars));
}
